import Jazzicon from 'jazzicon'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Copy, RefreshCw, Search } from 'react-feather'
import styled from 'styled-components'

import PairTokenLogos from '../../components/PairTokenLogos'
import {
  extractUniqueTokens,
  RewardPoolData,
  useHolderUSDValues,
  usePairOnChainData,
  usePairUSDValues,
  useRewardPoolData,
  useTokenPrices
} from '../../data/stats'
import {
  FarmStaker,
  formatUsd,
  getExplorerUrl,
  isFarmContract,
  shortenAddress,
  useFarmStakers,
  useSunsetData
} from '../../data/sunset'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 20px;
  margin: 0 auto;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.text1};
`

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 24px;
`

const IconRefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  background: ${({ theme }) => theme.bg2};
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ theme }) => theme.bg3};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`

const StatCard = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 20px;
  text-align: center;
`

const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 8px;
`

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 32px;
`

const SpinningIcon = styled(RefreshCw)<{ spinning: boolean }>`
  animation: ${({ spinning }) => (spinning ? 'spin 1s linear infinite' : 'none')};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const PairTableWrapper = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* Shared column widths for alignment across all row levels */
  --col-share: 180px;
  --col-tvl: 120px;
  --col-chevron: 40px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    --col-share: 100px;
    --col-tvl: 80px;
  `}
`

const PairRowContainer = styled.div<{ expanded: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.bg3};

  &:last-child {
    border-bottom: none;
  }
`

const PairRowHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr var(--col-share) var(--col-tvl) var(--col-chevron);
  align-items: center;
  gap: 16px;
  padding: 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.bg3};
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 8px;
  `}
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr var(--col-share) var(--col-tvl) var(--col-chevron);
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
  font-weight: 600;
  color: ${({ theme }) => theme.text2};
  font-size: 14px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 8px;
  `}
`

const PoolInfo = styled.div`
  display: grid;
  grid-template-columns: 36px 140px 1fr auto;
  align-items: center;
  gap: 12px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-template-columns: 36px auto 1fr;
    gap: 8px;
  `}
`

const PoolAddress = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.text3};

  .full-address {
    ${({ theme }) => theme.mediaWidth.upToSmall`
      display: none;
    `}
  }

  .short-address {
    display: none;
    ${({ theme }) => theme.mediaWidth.upToSmall`
      display: inline;
    `}
  }
`

const IdenticonWrapper = styled.div`
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.bg4};
  flex-shrink: 0;
`

const SkeletonLoader = styled.div<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '36px'};
  height: ${({ height }) => height || '24px'};
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.bg3} 25%,
    ${({ theme }) => theme.bg4} 50%,
    ${({ theme }) => theme.bg3} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const ChainBadge = styled.span<{ chain: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ chain }) => {
    switch (chain) {
      case 'polygon':
        return '#8247e5'
      case 'bnb':
        return '#f0b90b'
      case 'arbitrum':
        return '#28a0f0'
      default:
        return '#666'
    }
  }};
  color: ${({ chain }) => (chain === 'bnb' ? '#000' : '#fff')};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-column: 2 / -1;
    justify-self: start;
  `}
`

const PairName = styled.span`
  display: block;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TVLValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
`

const UnclaimedRewards = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text3};
  margin-top: 2px;

  span {
    display: inline-block;
    margin-right: 8px;
  }
`

const TVLCell = styled.div`
  min-width: 0;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AddressLink = styled.a`
  color: ${({ theme }) => theme.primary1};
  text-decoration: none;
  font-family: monospace;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
`

const ExpandedContent = styled.div`
  background: ${({ theme }) => theme.bg1};
  padding: 8px 0 8px 20px;
  border-top: 1px solid ${({ theme }) => theme.bg3};
`

const HoldersTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 12px;
`

const HolderRow = styled.div<{ isFarm?: boolean }>`
  display: grid;
  grid-template-columns: 1fr var(--col-share) var(--col-tvl) var(--col-chevron);
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.bg2};
  margin-bottom: 8px;
  cursor: ${({ isFarm }) => (isFarm ? 'pointer' : 'default')};

  &:last-child {
    margin-bottom: 0;
  }

  ${({ isFarm }) =>
    isFarm &&
    `
    &:hover {
      background: ${({ theme }: any) => theme.bg3};
    }
  `}

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 8px;
  `}
`

const HolderAddressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HolderAddress = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.text1};

  .full-address {
    ${({ theme }) => theme.mediaWidth.upToSmall`
      display: none;
    `}
  }

  .short-address {
    display: none;
    ${({ theme }) => theme.mediaWidth.upToSmall`
      display: inline;
    `}
  }
`

const ShareContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ProgressBarWrapper = styled.div`
  width: 80px;
  height: 8px;
  background: ${({ theme }) => theme.bg3};
  border-radius: 4px;
  overflow: hidden;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 40px;
  `}
`

const ProgressBarFill = styled.div<{ percent: number }>`
  width: ${({ percent }) => Math.min(percent, 100)}%;
  height: 100%;
  background: ${({ theme }) => theme.primary1};
  border-radius: 4px;
  margin-left: auto;
`

const SharePercent = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.text2};
  min-width: 45px;
`

const StakersContent = styled.div`
  background: ${({ theme }) => theme.bg1};
  padding: 8px 0 8px 20px;
  margin: 0 0 8px 0;
`

const StakersTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text3};
  margin-bottom: 8px;
`

const FarmBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: ${({ theme }) => theme.primary1};
  color: white;
  margin-left: 6px;
`

const StatusBadge = styled.span<{ status: 'active' | 'ended' | 'paused' }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 4px;
  background: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#00c853'
      case 'ended':
        return '#ff9800'
      case 'paused':
        return '#9e9e9e'
      default:
        return '#666'
    }
  }};
  color: white;
`

const UnclaimedBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  margin-left: 4px;
`

const FarmInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`

// Identicon component for addresses
function AddressIdenticon({ address, size = 20 }: { address: string; size?: number }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (address && ref.current) {
      ref.current.innerHTML = ''
      ref.current.appendChild(Jazzicon(size, parseInt(address.slice(2, 10), 16)))
    }
  }, [address, size])

  return <IdenticonWrapper ref={ref} style={{ width: size, height: size }} />
}

const NoHolders = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.text3};
  font-size: 14px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 40px;
  margin-bottom: 16px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: stretch;
  `}
`

const SectionTitleInline = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.text1};
`

const SearchWrapper = styled.div`
  position: relative;
  width: 400px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `}
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.text3};
  pointer-events: none;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 12px;
  background: ${({ theme }) => theme.bg2};
  color: ${({ theme }) => theme.text1};
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.text3};
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary1};
  }
`

const CopyButton = styled.button<{ copied?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme, copied }) => (copied ? '#00c853' : theme.text3)};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.bg3};
    color: ${({ theme, copied }) => (copied ? '#00c853' : theme.text1)};
  }
`

/* Commented out - used in Reward Pools section which is now nested
const PoolRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 150px 150px 100px;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};

  &:last-child {
    border-bottom: none;
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-template-columns: 80px 1fr 100px;
  `}
`

const PoolTableHeader = styled(PoolRow)`
  font-weight: 600;
  color: ${({ theme }) => theme.text2};
  font-size: 14px;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const StatusBadge = styled.span<{ status: 'active' | 'ended' | 'paused' | 'error' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#00c853'
      case 'ended':
        return '#ff9800'
      case 'paused':
        return '#9e9e9e'
      case 'error':
        return '#ff5252'
      default:
        return '#666'
    }
  }};
  color: white;
`

const RewardTokens = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const RewardToken = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  background: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text2};
`
*/

interface HolderData {
  holderAddress: string
  balance: string
  balanceUsd: number
  sharePercent: number
}

interface PairRowProps {
  pair: {
    chain: string
    chainId: number
    pairAddress: string
    pairSymbol: string
    token0Address: string
    token1Address: string
  }
  liveTvl?: number
  holders: HolderData[]
  rewardPools: RewardPoolData[]
  farmStakers: FarmStaker[]
  searchQuery?: string
  prices: { [tokenAddress: string]: number }
}

function FarmHolderRow({
  holder,
  chainId,
  rewardPool,
  farmStakers,
  totalPoolLp,
  totalPoolUsd,
  searchQuery,
  prices
}: {
  holder: HolderData
  chainId: number
  rewardPool?: RewardPoolData
  farmStakers: FarmStaker[]
  totalPoolLp: number
  totalPoolUsd: number
  searchQuery?: string
  prices: { [tokenAddress: string]: number }
}) {
  const [manualStakersExpanded, setManualStakersExpanded] = useState(false)

  const getStatus = (): 'active' | 'ended' | 'paused' => {
    if (!rewardPool) return 'ended'
    if (rewardPool.paused) return 'paused'
    const hasActiveRewards = rewardPool.rewardTokens.some(t => t.isActive)
    return hasActiveRewards ? 'active' : 'ended'
  }

  // Filter stakers for this farm on this chain
  const relevantStakers = farmStakers.filter(
    s => s.farmAddress.toLowerCase() === holder.holderAddress.toLowerCase() && s.chainId === chainId
  )

  // Check if search is targeting stakers
  const isSearchingStakers = useMemo(() => {
    if (!searchQuery?.trim()) return false
    const query = searchQuery.toLowerCase().trim()
    // Check if any staker matches the query
    return relevantStakers.some(s => s.userAddress.toLowerCase().includes(query))
  }, [searchQuery, relevantStakers])

  // Filter stakers based on search query
  const filteredStakers = useMemo(() => {
    if (!searchQuery?.trim() || !isSearchingStakers) return relevantStakers
    const query = searchQuery.toLowerCase().trim()
    return relevantStakers.filter(s => s.userAddress.toLowerCase().includes(query))
  }, [relevantStakers, searchQuery, isSearchingStakers])

  // Auto-expand when search matches a staker
  const stakersExpanded = manualStakersExpanded || isSearchingStakers

  // Calculate price per LP token
  const pricePerLp = totalPoolLp > 0 ? totalPoolUsd / totalPoolLp : 0

  // Calculate total claimable vs earned rewards for this pool
  const stakerRewards = rewardPool?.stakerRewards
  const rewardTotals = useMemo(() => {
    if (!stakerRewards) return { claimable: [], earned: [] }
    const claimableTotals: Record<string, { symbol: string; total: number; address: string }> = {}
    const earnedTotals: Record<string, { symbol: string; total: number; address: string }> = {}
    for (const sr of stakerRewards) {
      for (const r of sr.rewards) {
        if (!claimableTotals[r.tokenAddress]) {
          claimableTotals[r.tokenAddress] = {
            symbol: r.tokenSymbol,
            total: 0,
            address: r.tokenAddress
          }
          earnedTotals[r.tokenAddress] = {
            symbol: r.tokenSymbol,
            total: 0,
            address: r.tokenAddress
          }
        }
        claimableTotals[r.tokenAddress].total += r.claimableFormatted
        earnedTotals[r.tokenAddress].total += r.earnedFormatted
      }
    }
    return { claimable: Object.values(claimableTotals), earned: Object.values(earnedTotals) }
  }, [stakerRewards])

  // Calculate total USD value of claimable rewards
  const totalClaimableUsd = useMemo(() => {
    return rewardTotals.claimable.reduce((sum, t) => {
      const tokenPrice = prices[t.address.toLowerCase()] || 0
      return sum + t.total * tokenPrice
    }, 0)
  }, [rewardTotals.claimable, prices])

  return (
    <>
      <HolderRow isFarm onClick={() => setManualStakersExpanded(!manualStakersExpanded)}>
        <HolderAddressContainer>
          <AddressIdenticon address={holder.holderAddress} />
          <FarmInfo>
            <HolderAddress>
              <AddressLink
                href={getExplorerUrl(chainId, holder.holderAddress)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
              >
                <span className="full-address">{holder.holderAddress}</span>
                <span className="short-address">{shortenAddress(holder.holderAddress)}</span>
              </AddressLink>
              <CopyButton
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigator.clipboard.writeText(holder.holderAddress)
                }}
                title="Copy address"
              >
                <Copy size={12} />
              </CopyButton>
            </HolderAddress>
            <FarmBadge>REWARD POOL</FarmBadge>
            <StatusBadge status={getStatus()}>{getStatus().toUpperCase()}</StatusBadge>
            {rewardTotals.claimable.some(t => t.total > 0) && (
              <UnclaimedBadge>
                Claimable:{' '}
                {rewardTotals.claimable.map(t => `${t.total.toFixed(2)} ${t.symbol}`).join(', ')}
                {totalClaimableUsd > 0 && ` (${formatUsd(totalClaimableUsd)})`}
              </UnclaimedBadge>
            )}
          </FarmInfo>
        </HolderAddressContainer>
        <ShareContainer>
          <ProgressBarWrapper>
            <ProgressBarFill percent={holder.sharePercent} />
          </ProgressBarWrapper>
          <SharePercent>{holder.sharePercent.toFixed(1)}%</SharePercent>
        </ShareContainer>
        <TVLCell>
          <TVLValue>{formatUsd(holder.balanceUsd)}</TVLValue>
        </TVLCell>
        <div>{stakersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
      </HolderRow>
      {stakersExpanded && (
        <StakersContent>
          <StakersTitle>
            Stakers in Reward Pool (
            {isSearchingStakers
              ? `${filteredStakers.length} of ${relevantStakers.length}`
              : relevantStakers.length}
            )
          </StakersTitle>
          {filteredStakers.length > 0 ? (
            filteredStakers
              .sort((a, b) => b.stakedBalanceFormatted - a.stakedBalanceFormatted)
              .map((staker, i) => {
                const stakerUsd = staker.stakedBalanceFormatted * pricePerLp
                const stakerPercent =
                  totalPoolLp > 0 ? (staker.stakedBalanceFormatted / totalPoolLp) * 100 : 0
                // Look up earned rewards for this staker
                const stakerReward = rewardPool?.stakerRewards.find(
                  sr => sr.userAddress.toLowerCase() === staker.userAddress.toLowerCase()
                )
                return (
                  <HolderRow key={i} style={{ marginBottom: 8 }}>
                    <HolderAddressContainer>
                      <AddressIdenticon address={staker.userAddress} />
                      <HolderAddress>
                        <AddressLink
                          href={getExplorerUrl(chainId, staker.userAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="full-address">{staker.userAddress}</span>
                          <span className="short-address">
                            {shortenAddress(staker.userAddress)}
                          </span>
                        </AddressLink>
                        <CopyButton
                          onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            navigator.clipboard.writeText(staker.userAddress)
                          }}
                          title="Copy address"
                        >
                          <Copy size={12} />
                        </CopyButton>
                      </HolderAddress>
                    </HolderAddressContainer>
                    <ShareContainer>
                      <ProgressBarWrapper>
                        <ProgressBarFill percent={stakerPercent} />
                      </ProgressBarWrapper>
                      <SharePercent>{stakerPercent.toFixed(1)}%</SharePercent>
                    </ShareContainer>
                    <TVLCell>
                      <TVLValue>{formatUsd(stakerUsd)}</TVLValue>
                      {stakerReward &&
                        stakerReward.rewards.some(r => r.claimableFormatted > 0) &&
                        (() => {
                          const rewardUsdTotal = stakerReward.rewards.reduce((sum, r) => {
                            const tokenPrice = prices[r.tokenAddress.toLowerCase()] || 0
                            return sum + r.claimableFormatted * tokenPrice
                          }, 0)
                          return (
                            <UnclaimedRewards>
                              {stakerReward.rewards
                                .filter(r => r.claimableFormatted > 0)
                                .map((r, ri) => (
                                  <span key={ri}>
                                    {r.claimableFormatted.toFixed(4)} {r.tokenSymbol}
                                  </span>
                                ))}
                              {rewardUsdTotal > 0 && <span>({formatUsd(rewardUsdTotal)})</span>}
                            </UnclaimedRewards>
                          )
                        })()}
                    </TVLCell>
                    <div></div>
                  </HolderRow>
                )
              })
          ) : (
            <div style={{ color: '#888', fontSize: 12, padding: '8px 0' }}>
              {isSearchingStakers ? 'No matching stakers found' : 'No stakers found'}
            </div>
          )}
        </StakersContent>
      )}
    </>
  )
}

function PairRow({
  pair,
  liveTvl,
  holders,
  rewardPools,
  farmStakers,
  searchQuery,
  prices
}: PairRowProps) {
  const [manualExpanded, setManualExpanded] = useState(false)

  // Check if search is targeting holders or stakers (not pool symbol, chain, or pool address)
  const isSearchingHolders = useMemo(() => {
    if (!searchQuery?.trim()) return false
    const query = searchQuery.toLowerCase().trim()

    // If query matches pool-level properties, not searching for holders
    if (pair.pairSymbol.toLowerCase().includes(query)) return false
    if (pair.chain.toLowerCase().includes(query)) return false
    if (pair.pairAddress.toLowerCase().includes(query)) return false

    return true
  }, [searchQuery, pair.pairSymbol, pair.chain, pair.pairAddress])

  // Check if search matches any stakers in this pool's farms
  const hasStakerMatch = useMemo(() => {
    if (!searchQuery?.trim() || !isSearchingHolders) return false
    const query = searchQuery.toLowerCase().trim()

    // Check stakers in farms for this pool
    const farmHolders = holders.filter(h => isFarmContract(h.holderAddress))
    for (const farmHolder of farmHolders) {
      const stakersInFarm = farmStakers.filter(
        s =>
          s.farmAddress.toLowerCase() === farmHolder.holderAddress.toLowerCase() &&
          s.chainId === pair.chainId
      )
      if (stakersInFarm.some(s => s.userAddress.toLowerCase().includes(query))) return true
    }
    return false
  }, [searchQuery, isSearchingHolders, holders, farmStakers, pair.chainId])

  // Filter holders based on search query - but include farm holders if they have matching stakers
  const filteredHolders = useMemo(() => {
    if (!searchQuery?.trim() || !isSearchingHolders) return holders
    const query = searchQuery.toLowerCase().trim()

    return holders.filter(h => {
      // Direct holder match
      if (h.holderAddress.toLowerCase().includes(query)) return true

      // If it's a farm, check if any stakers match
      if (isFarmContract(h.holderAddress)) {
        const stakersInFarm = farmStakers.filter(
          s =>
            s.farmAddress.toLowerCase() === h.holderAddress.toLowerCase() &&
            s.chainId === pair.chainId
        )
        if (stakersInFarm.some(s => s.userAddress.toLowerCase().includes(query))) return true
      }

      return false
    })
  }, [holders, searchQuery, isSearchingHolders, farmStakers, pair.chainId])

  // Show filtered count vs total only if actually filtering holders
  const isFilteringHolders = isSearchingHolders && filteredHolders.length !== holders.length
  const holderCountDisplay = isFilteringHolders
    ? `${filteredHolders.length} of ${holders.length}`
    : holders.length.toString()

  // Auto-expand when search matches a holder or staker address
  const hasHolderMatch = isSearchingHolders && filteredHolders.length > 0
  const expanded = manualExpanded || hasHolderMatch || hasStakerMatch

  return (
    <PairRowContainer expanded={expanded}>
      <PairRowHeader onClick={() => setManualExpanded(!manualExpanded)}>
        <PoolInfo>
          <div>
            <PairTokenLogos
              token0Address={pair.token0Address}
              token1Address={pair.token1Address}
              size={24}
            />
          </div>
          <PairName>{pair.pairSymbol}</PairName>
          <PoolAddress>
            <AddressLink
              href={getExplorerUrl(pair.chainId, pair.pairAddress)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              <span className="full-address">{pair.pairAddress}</span>
              <span className="short-address">{shortenAddress(pair.pairAddress)}</span>
            </AddressLink>
            <CopyButton
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(pair.pairAddress)
              }}
              title="Copy address"
            >
              <Copy size={12} />
            </CopyButton>
          </PoolAddress>
          <ChainBadge chain={pair.chain}>{pair.chain.toUpperCase()}</ChainBadge>
        </PoolInfo>
        <div></div>
        <TVLCell>
          {liveTvl !== undefined ? (
            <TVLValue>{formatUsd(liveTvl)}</TVLValue>
          ) : (
            <SkeletonLoader width="80px" height="20px" />
          )}
        </TVLCell>
        <div>{expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</div>
      </PairRowHeader>

      {expanded && (
        <ExpandedContent>
          <HoldersTitle>Liquidity Providers ({holderCountDisplay})</HoldersTitle>
          {filteredHolders.length > 0 ? (
            filteredHolders
              .sort((a, b) => b.balanceUsd - a.balanceUsd)
              .map((holder, i) => {
                const isFarm = isFarmContract(holder.holderAddress)

                if (isFarm) {
                  // Find matching reward pool by address AND chainId
                  const rewardPool = rewardPools.find(
                    pool =>
                      pool.address.toLowerCase() === holder.holderAddress.toLowerCase() &&
                      pool.chainId === pair.chainId
                  )
                  // Calculate total LP in pool from all holders
                  const totalPoolLp = holders.reduce((sum, h) => sum + parseFloat(h.balance), 0)
                  return (
                    <FarmHolderRow
                      key={i}
                      holder={holder}
                      chainId={pair.chainId}
                      rewardPool={rewardPool}
                      farmStakers={farmStakers}
                      totalPoolLp={totalPoolLp}
                      totalPoolUsd={liveTvl || 0}
                      searchQuery={searchQuery}
                      prices={prices}
                    />
                  )
                }

                return (
                  <HolderRow key={i}>
                    <HolderAddressContainer>
                      <AddressIdenticon address={holder.holderAddress} />
                      <HolderAddress>
                        <AddressLink
                          href={getExplorerUrl(pair.chainId, holder.holderAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="full-address">{holder.holderAddress}</span>
                          <span className="short-address">
                            {shortenAddress(holder.holderAddress)}
                          </span>
                        </AddressLink>
                        <CopyButton
                          onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            navigator.clipboard.writeText(holder.holderAddress)
                          }}
                          title="Copy address"
                        >
                          <Copy size={12} />
                        </CopyButton>
                      </HolderAddress>
                    </HolderAddressContainer>
                    <ShareContainer>
                      <ProgressBarWrapper>
                        <ProgressBarFill percent={holder.sharePercent} />
                      </ProgressBarWrapper>
                      <SharePercent>{holder.sharePercent.toFixed(1)}%</SharePercent>
                    </ShareContainer>
                    <TVLCell>
                      <TVLValue>{formatUsd(holder.balanceUsd)}</TVLValue>
                    </TVLCell>
                    <div></div>
                  </HolderRow>
                )
              })
          ) : (
            <NoHolders>
              {isFilteringHolders ? 'No matching holders found' : 'No holders found for this pair'}
            </NoHolders>
          )}
        </ExpandedContent>
      )}
    </PairRowContainer>
  )
}

export default function Stats() {
  const { pairs, holders } = useSunsetData()
  const farmStakers = useFarmStakers()
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Hijack Cmd+F / Ctrl+F to focus the in-app search bar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        if (searchInputRef.current) {
          searchInputRef.current.focus()
          searchInputRef.current.select()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Fetch on-chain data
  const { pairData, holderData, loading: onChainLoading, refetch } = usePairOnChainData(
    pairs,
    holders
  )

  // Fetch reward pool data
  const { pools: rewardPools, loading: poolsLoading, refetch: refetchPools } = useRewardPoolData()

  // Extract unique tokens for price fetching
  const tokensByChain = useMemo(() => extractUniqueTokens(pairData), [pairData])

  // Fetch token prices
  const { prices, loading: pricesLoading } = useTokenPrices(tokensByChain)

  // Calculate USD values
  const pairValues = usePairUSDValues(pairData, prices)
  const holderValues = useHolderUSDValues(holderData, pairData, pairValues)

  // Calculate totals from live data only
  const totalTvl = useMemo(() => {
    return Object.values(pairValues).reduce((sum, p) => sum + p.tvlUsd, 0)
  }, [pairValues])

  const totalHolders = useMemo(() => {
    const uniqueAddresses = new Set(holderValues.map(h => h.holderAddress.toLowerCase()))
    return uniqueAddresses.size
  }, [holderValues])

  const activePairs = useMemo(() => {
    return Object.keys(pairValues).length
  }, [pairValues])

  const loading = onChainLoading || pricesLoading || poolsLoading

  // Combined refetch
  const handleRefresh = () => {
    refetch()
    refetchPools()
  }

  // Group holders by pair for expanded view
  const holdersByPair = useMemo(() => {
    const grouped: { [pairAddress: string]: typeof holderValues } = {}
    for (const holder of holderValues) {
      const key = holder.pairAddress.toLowerCase()
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(holder)
    }
    return grouped
  }, [holderValues])

  // Filter pairs based on search query
  const filteredPairs = useMemo(() => {
    if (!searchQuery.trim()) return pairs

    const query = searchQuery.toLowerCase().trim()

    return pairs.filter(pair => {
      // Match pool symbol
      if (pair.pairSymbol.toLowerCase().includes(query)) return true

      // Match pool address
      if (pair.pairAddress.toLowerCase().includes(query)) return true

      // Match chain name
      if (pair.chain.toLowerCase().includes(query)) return true

      // Match holder addresses in this pool
      const pairHolders = holdersByPair[pair.pairAddress.toLowerCase()] || []
      if (pairHolders.some(h => h.holderAddress.toLowerCase().includes(query))) return true

      // Match staker addresses in farms for this pool
      const farmHolders = pairHolders.filter(h => isFarmContract(h.holderAddress))
      for (const farmHolder of farmHolders) {
        const farmStakersForPool = farmStakers.filter(
          s =>
            s.farmAddress.toLowerCase() === farmHolder.holderAddress.toLowerCase() &&
            s.chainId === pair.chainId
        )
        if (farmStakersForPool.some(s => s.userAddress.toLowerCase().includes(query))) return true
      }

      return false
    })
  }, [pairs, searchQuery, holdersByPair, farmStakers])

  return (
    <PageWrapper>
      <TitleRow>
        <Title>Protocol Statistics</Title>
        <IconRefreshButton
          onClick={handleRefresh}
          disabled={loading}
          title={loading ? 'Loading...' : 'Refresh Data'}
        >
          <SpinningIcon size={18} spinning={loading} />
        </IconRefreshButton>
      </TitleRow>
      <Subtitle>Real-time on-chain data for all XATA pairs and LP holders.</Subtitle>

      {/* Stats */}
      <StatsGrid>
        <StatCard>
          <StatLabel>Total Value Locked</StatLabel>
          <StatValue>
            {loading ? <SkeletonLoader width="100px" height="28px" /> : formatUsd(totalTvl)}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Active Pairs</StatLabel>
          <StatValue>
            {loading ? <SkeletonLoader width="60px" height="28px" /> : activePairs}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Liquidity Providers</StatLabel>
          <StatValue>
            {loading ? <SkeletonLoader width="60px" height="28px" /> : totalHolders}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Reward Pool Stakers</StatLabel>
          <StatValue>{farmStakers.length}</StatValue>
        </StatCard>
      </StatsGrid>

      {/* Pairs Table */}
      <SectionHeader>
        <SectionTitleInline>Liquidity Pools</SectionTitleInline>
        <SearchWrapper>
          <SearchIcon size={16} />
          <SearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search by pool, address, or holder... (⌘F)"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </SearchWrapper>
      </SectionHeader>
      <PairTableWrapper>
        <TableHeader>
          <div>Pool</div>
          <div></div>
          <TVLCell>TVL</TVLCell>
          <div></div>
        </TableHeader>
        {filteredPairs
          .sort((a, b) => {
            const tvlA = pairValues[a.pairAddress.toLowerCase()]?.tvlUsd ?? 0
            const tvlB = pairValues[b.pairAddress.toLowerCase()]?.tvlUsd ?? 0
            return tvlB - tvlA
          })
          .map((pair, i) => (
            <PairRow
              key={i}
              pair={pair}
              liveTvl={pairValues[pair.pairAddress.toLowerCase()]?.tvlUsd}
              holders={holdersByPair[pair.pairAddress.toLowerCase()] || []}
              rewardPools={rewardPools}
              farmStakers={farmStakers}
              searchQuery={searchQuery}
              prices={prices}
            />
          ))}
      </PairTableWrapper>

      {/* Reward Pools - commented out, now nested inside liquidity pool holders
      <SectionTitle>Reward Pools</SectionTitle>
      <PairTableWrapper>
        <PoolTableHeader>
          <div>Chain</div>
          <div>Pool</div>
          <div>Total Staked</div>
          <HiddenOnMobile>Address</HiddenOnMobile>
          <HiddenOnMobile>Status</HiddenOnMobile>
        </PoolTableHeader>
        {rewardPools.length > 0 ? (
          rewardPools.map((pool, i) => {
            const hasActiveRewards = pool.rewardTokens.some(t => t.isActive)
            const getStatus = (): 'active' | 'ended' | 'paused' | 'error' => {
              if (pool.error) return 'error'
              if (pool.paused) return 'paused'
              if (hasActiveRewards) return 'active'
              return 'ended'
            }
            const status = getStatus()
            return (
              <PoolRow key={i}>
                <div>
                  <ChainBadge chain={pool.chain.toLowerCase()}>
                    {pool.chain.toUpperCase()}
                  </ChainBadge>
                </div>
                <div>
                  <PairName>{pool.symbol}</PairName>
                  {pool.error ? (
                    <div style={{ fontSize: 11, color: '#ff5252', marginTop: 4 }}>{pool.error}</div>
                  ) : pool.rewardTokens.length > 0 ? (
                    <RewardTokens style={{ marginTop: 4 }}>
                      {pool.rewardTokens.map((token, j) => (
                        <RewardToken key={j}>{token.symbol}</RewardToken>
                      ))}
                    </RewardTokens>
                  ) : null}
                </div>
                <div>
                  <TVLValue>
                    {pool.error
                      ? '-'
                      : `${pool.totalStakedFormatted.toFixed(2)} LP`}
                  </TVLValue>
                </div>
                <HiddenOnMobile>
                  <AddressLink
                    href={getExplorerUrl(pool.chainId, pool.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {shortenAddress(pool.address)}
                  </AddressLink>
                </HiddenOnMobile>
                <HiddenOnMobile>
                  <StatusBadge status={status}>
                    {status.toUpperCase()}
                  </StatusBadge>
                </HiddenOnMobile>
              </PoolRow>
            )
          })
        ) : (
          <PoolRow>
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#888' }}>
              {poolsLoading ? 'Loading reward pools...' : 'No reward pools found'}
            </div>
          </PoolRow>
        )}
      </PairTableWrapper>
      */}
    </PageWrapper>
  )
}
